import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWindowSize } from 'react-use';

import { Badge } from 'antd';
import Axios from 'axios';

import { isBookingEvent } from '@libs/EventTypes';
import { isModelBusy } from '@libs/ModelAvailability';
import { getMoment } from '@libs/NwMoment';

import { useLayoutContext } from '@contexts/LayoutContext';
import { useStateValue } from '@contexts/StateProvider';
import { useUserPermission } from '@contexts/UserContext';

import { modelFullName } from '@hooks/API/Contact/UseContactsList';

import ModelDetailsAge from '@components/ContactDetails/ModelDetails/CommonComponents/ModelDetailsHeader/ModelDetailsAge';
import ModelDetailsHeaderAgencies from '@components/ContactDetails/ModelDetails/CommonComponents/ModelDetailsHeader/ModelDetailsHeaderAgencies';

import ContactAlertPopover from '@components/ContactAlert/ContactAlertPopover';
import ModelDrawer from '@components/ContactDetails/ModelDetails/Drawer/ModelDrawer';
import FolderModelsTableColumn from '@components/ContactsFolders/FolderModelsTableColumn';
import {
    useContactsSelector,
    useContactsSelectorDispatch,
} from '@components/ContactsSelector/Common/ContactsSelectorContext';
import ModelAvailabilityIcon from '@components/ContactsSelector/ModelsSelector/ModelsAvailability/ModelAvailabilityIcon';
import ModelsTableColumnEmails from '@components/ContactsSelector/ModelsSelector/ModelsWall/ModelsTable/ModelsTableColumnEmails';
import ModelsTableColumnNameMobile from '@components/ContactsSelector/ModelsSelector/ModelsWall/ModelsTable/ModelsTableColumnNameMobile';
import useFilteredFeatureIDs from '@components/ContactsSelector/ModelsSelector/ModelsWall/UseFilteredFeatureIDs';
import PreviousEventsButton from '@components/EventDrawer/EventDrawerUi/PreviousEventsButton';
import FeaturesDrawerNoData from '@components/Features/FeaturesDrawerNoData';
import MainFeatures from '@components/Features/MainFeatures';
import { Loading } from '@components/Gui/Loading';
import NwIcon from '@components/Gui/NwIcon';
import { NwTag } from '@components/Gui/NwTag';
import StyleVariables from '@components/Gui/StyleVariables';
import { ModelCover } from '@components/ModelCover';
import ModelInTown from '@components/ModelInTown/ModelInTown';

import ShowSeasonModelsTableColumn from '@containers/Booking/ShowSeasons/ShowSeasonModelsTableColumn';
import ShowSeasonModelsTableColumnModel from '@containers/Booking/ShowSeasons/ShowSeasonModelsTableColumnModel';
import ShowSeasonModelWallInfo from '@containers/Booking/ShowSeasons/ShowSeasonModelWallInfo';

import ModelsTableColumnEvent from './ModelsTableColumnEvent';
import ModelsTableColumnNonBookingEvent from './ModelsTableColumnNonBookingEvent';
import ModelsTableColumnPackage from './ModelsTableColumnPackage';
// import ModelsTableColumnPackageMobile from './ModelsTableColumnPackageMobile';
import {
    CalendarActions,
    FeaturesContainer,
    ModelCoverWrapper,
    NameCell,
    StyledModelsTable,
    TagsContainer,
} from './ModelsTableUI';
import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

/**
 * Nel drawer la tabella ha molto meno spazio della pagina piena: sotto questa larghezza
 * la colonna delle features non sta più accanto alle icone di azione del package, e il
 * suo contenuto scende sotto al nome della modella.
 */
const DRAWER_COMPACT_MAX_WIDTH = 1300;

const ModelsTable = ({
    addModelsAs,
    list,
    //packageGroup,
}) => {
    const navigate = useNavigate();

    const { selection, context, layout, actions, cache } = useContactsSelector();
    const { activeSet, listType, quickFilters } = selection;
    const { area, currentEvent, currentPackage, scope } = context;
    const { itemClickAction, multipleSelectionActive, selectedItemsIDs, isFetchingAvailability } = actions;
    const { modelAvailability } = cache;
    const filteredFeatureIDs = useFilteredFeatureIDs();

    const availabilityInfo = useMemo(() => {
        return {
            eventDatesIncluded: modelAvailability && modelAvailability.eventDatesIncluded ? true : false,
            isFetching: isFetchingAvailability,
            isActive: modelAvailability,
        };
    }, [modelAvailability, isFetchingAvailability]);

    const usePagination = list.length > 500;

    const showID = quickFilters && quickFilters.sort && quickFilters.sort === 'ID';

    const cs_dispatch = useContactsSelectorDispatch();

    // Filter and order Main Features depending on User Metrics Config
    const [availabilityByModel, setAvailabilityByModel] = useState({});
    const [pagedList, setPagedList] = useState([]);

    const layoutContext = useLayoutContext();
    const { breakpoints, isMobile } = layoutContext;
    const { width: windowWidth } = useWindowSize();

    // Vale solo per il package nel drawer: la tabella a pagina piena resta com'è.
    const isPackageDrawerCompact = scope === 'package' && layout.drawer && windowWidth < DRAWER_COMPACT_MAX_WIDTH;

    const [isOpenDrawer, setIsOpenDrawer] = useState(false);
    const [drawerId, setDrawerId] = useState();
    const [selectedTab, setSelectedTab] = useState('profile');
    const [{ areaContext }] = useStateValue();
    const [loadMore, setLoadMore] = useState(false);
    const [newlyAddedToPackage, setNewlyAddedToPackage] = useState([]);
    const [isOpenFeatureDrawer, setIsOpenFeatureDrawer] = useState(false);

    const handleAddBooksToPackage = (modelId, bookIDs) => {
        //find the model in existing array and update the playlistIDs
        const found = newlyAddedToPackage.find((item) => item.modelID === modelId);
        if (found) {
            const newPlaylistIDs = [...found.playlistIDs, ...bookIDs];
            const newModels = newlyAddedToPackage.map((item) => {
                if (item.modelID === modelId) {
                    return { ...item, playlistIDs: newPlaylistIDs };
                }
                return item;
            });
            setNewlyAddedToPackage(newModels);
            return;
        } else {
            setNewlyAddedToPackage([...newlyAddedToPackage, { modelID: modelId, playlistIDs: bookIDs }]);
        }
    };

    const handleRemoveBookFromPackage = (modelId, bookID) => {
        const found = newlyAddedToPackage.find((item) => item.modelID === modelId);
        if (found) {
            const newPlaylistIDs = found.playlistIDs.filter((id) => id !== bookID);
            if (newPlaylistIDs.length === 0) {
                const newModels = newlyAddedToPackage.filter((item) => item.modelID !== modelId);
                setNewlyAddedToPackage(newModels);
            } else {
                const newModels = newlyAddedToPackage.map((item) => {
                    if (item.modelID === modelId) {
                        return { ...item, playlistIDs: newPlaylistIDs };
                    }
                    return item;
                });
                setNewlyAddedToPackage(newModels);
            }
        }
    };

    const loader = useRef(null);

    const handleObserver = useCallback((entries) => {
        const target = entries[0];
        if (target.isIntersecting) {
            setLoadMore(true);
        }
    }, []);

    useEffect(() => {
        const option = {
            root: null,
            rootMargin: '10px',
            threshold: 0,
        };
        const observer = new IntersectionObserver(handleObserver, option);
        if (loader.current) {
            observer.observe(loader.current);
        }
    }, [handleObserver]);

    const pageCardCount = usePagination ? 50 : list.length;

    useEffect(() => {
        setPagedList(list.slice(0, pageCardCount));
    }, [list]);

    useEffect(() => {
        if (loadMore) {
            if (list.length > pagedList.length) {
                const newShowedList = list.slice(pagedList.length, pagedList.length + pageCardCount);
                setPagedList([...pagedList, ...newShowedList]);
            }
            setLoadMore(false);
        }
    }, [loadMore]);

    const dataSource = useMemo(() => {
        const pds = pagedList.map((model) => ({
            ...model,
            key: model.ID,
            name: modelFullName(model),
            agency: model.MAN,
            birthday: model.B,
            features: model,
            tags: model.T.map((el) => el.Name),
            image: model,
            action: model.ID,
            ...model,
        }));
        return pds;
    }, [pagedList]);

    const handleOpenDrawer = (modelId, tab = 'profile') => {
        setDrawerId(modelId);
        setSelectedTab(tab);
        setIsOpenDrawer(true);
    };

    const handleCloseDrawer = () => {
        setIsOpenDrawer(false);
        setDrawerId();
    };

    const isModelInCurrentEvent = (modelId) => {
        if (currentEvent && currentEvent.EventDates && currentEvent.EventDates.length === 1) {
            return currentEvent.EventDates[0].EventDatesModels.some((model) => model.ModelID === modelId);
        }
        return false;
    };

    const isModelInCurrentNonBookingEvent = (modelId) => {
        let modelProp = 'ModelID';
        if (currentEvent.TypeName === 'ToDo') {
            modelProp = 'ContactID';
        }
        if (Number(currentEvent[modelProp]) === Number(modelId)) {
            return true;
        }
        if (currentEvent.RelatedEvents) {
            return currentEvent.RelatedEvents.some((event) => Number(event[modelProp]) === Number(modelId));
        }
    };

    const isInConflict = (event, eventDate) => {
        const firstDate = getMoment(event.FromDate).startOf('day');
        const lastDate = getMoment(event.ToDate).endOf('day');
        return firstDate.isSameOrBefore(eventDate.ToDate) && lastDate.isSameOrAfter(eventDate.FromDate);
    };

    const isSameOfEventDate = (event, date) => {
        const eventDate = getMoment(event.FromDate).startOf('day');
        const dateDate = getMoment(date).startOf('day');
        return eventDate.isSame(dateDate);
    };

    useEffect(() => {
        let updatedAvailabilityByModel = {};
        for (const model of list) {
            let isBusy = null;
            let isFetched = !isFetchingAvailability;
            let inEvent = 0;
            let eventDates = [];

            const currentModelAvailability = modelAvailability?.result
                ? modelAvailability.result.find((modelAv) => modelAv.ModelID === model.ID)
                : null;

            // if (modelAvailability && modelAvailability.ModelsInvolved.find((idModel) => idModel === model.ID)) {
            //     isBusy = isModelBusy(modelAvailability.Events, model.ID, currentEvent);
            // }

            if (modelAvailability && modelAvailability.result) {
                if (currentModelAvailability) {
                    isBusy = true;
                } else {
                    isBusy = false;
                }
            }

            if (currentEvent) {
                if (
                    isBookingEvent(currentEvent.TypeName) &&
                    currentEvent.EventDates &&
                    currentEvent.EventDates.length === 1
                ) {
                    if (isModelInCurrentEvent(model.ID)) {
                        inEvent = 2;
                    }
                } else {
                    if (isModelInCurrentNonBookingEvent(model.ID)) {
                        inEvent = 2;
                    }
                }
                if (currentEvent.EventDates && currentEvent.EventDates.length > 1) {
                    let isInSomeEventDates = false;
                    let isInAllEventDates = true;
                    for (const ed of currentEvent.EventDates) {
                        const modelDateAvailability = {
                            date: ed.FromDate,
                            inEvent: 0,
                            isBusy: null,
                        };
                        if (modelAvailability && modelAvailability.result) {
                            if (currentModelAvailability) {
                                modelDateAvailability.isBusy =
                                    currentModelAvailability.ConflictDates.filter((cd) => isSameOfEventDate(ed, cd))
                                        .length > 0
                                        ? true
                                        : false;
                            } else {
                                modelDateAvailability.isBusy = false;
                            }
                        }
                        if (ed.EventDatesModels.some((edm) => Number(edm.ModelID) === Number(model.ID))) {
                            modelDateAvailability.inEvent = 2;
                            isInSomeEventDates = true;
                        } else {
                            isInAllEventDates = false;
                        }
                        eventDates.push(modelDateAvailability);
                    }
                    if (isInAllEventDates) {
                        inEvent = 2;
                    } else if (isInSomeEventDates) {
                        inEvent = 1;
                    }
                }
            }
            updatedAvailabilityByModel = {
                ...updatedAvailabilityByModel,
                [model.ID]: {
                    isBusy: isBusy,
                    isFetched: isFetched,
                    inEvent: inEvent,
                    eventDates: eventDates,
                },
            };
        }
        setAvailabilityByModel(updatedAvailabilityByModel);
    }, [modelAvailability, list, currentEvent]);

    const handleClickFeatures = (key) => {
        if (multipleSelectionActive) {
            cs_dispatch({ type: 'ToggleSelectedItem', ID: key });
        } else {
            setIsOpenFeatureDrawer(key);
        }
    };

    const handleClick = (key) => {
        if (multipleSelectionActive) {
            cs_dispatch({ type: 'ToggleSelectedItem', ID: key });
        }
        if (isMobile) {
            handleOpenDrawer(key);
            return;
        }
        if (itemClickAction === 'preview-drawer') {
            handleOpenDrawer(key);
        }
        if (itemClickAction === 'navigate') {
            window.scrollTo(0, 0);
            if (listType && activeSet) {
                if (area === 'accounting') {
                    navigate(`/accounting/model/${key}`);
                } else {
                    navigate(`/model/${key}/${listType}/${activeSet}`);
                }
            } else {
                navigate(`/model/${key}`);
            }
        }
        //onSelectModel(key);
    };

    const getPlaylistCount = (model) => {
        if (!layout.drawer) return 0;
        if (currentPackage && currentPackage.Models) {
            let pc = 0;
            const found = currentPackage.Models.find((item) => item.ModelID === model.ID);
            const foundInNewlyAdded = newlyAddedToPackage.find((item) => item.modelID === model.ID);
            if (found && found.Playlists) {
                pc += found.Playlists.length;
            }
            if (foundInNewlyAdded) {
                pc += foundInNewlyAdded.playlistIDs.length;
            }
            return pc;
        }
        return 0;
    };

    /**
     * Il corpo della colonna features. Vive in una funzione perché lo rendono in due
     * posti: la sua colonna, e la cella del nome quando il drawer è stretto.
     */
    const renderModelSummary = (model, { smaller, showInTown }) => (
        <>
            {model.B && <ModelDetailsAge birthday={model.B} nofixedspace small />}
            <FeaturesContainer $smaller={smaller} onClick={() => handleClickFeatures(model.ID)}>
                <MainFeatures contact={model} showPublicValues highlightedFeatureIDs={filteredFeatureIDs} />
            </FeaturesContainer>
            <ShowSeasonModelWallInfo modelId={model.ID} variant="table" />
            {showInTown && (
                <div className="model-in-town">
                    <ModelInTown modelID={model.ID} modelInTownSchedule={model.ITS} />
                </div>
            )}
        </>
    );

    const thumbnailColumn = {
        title: 'Image',
        dataIndex: 'image',
        key: 'image',
        width: isMobile ? '4rem' : '7rem',
        className: 'cover-image',
        render: (model) => {
            const selected = selectedItemsIDs.find((modelId) => modelId === model.ID);
            const playlistCount = getPlaylistCount(model);
            return (
                <ModelCoverWrapper selected={multipleSelectionActive && selected}>
                    <Badge count={playlistCount} color={StyleVariables.PrimaryColor}>
                        <ModelCover maxWidth={60} model={model} classN="model-cover-small" />
                    </Badge>
                </ModelCoverWrapper>
            );
        },
        onCell: (record, rowIndex) => {
            return {
                onClick: (event) => {
                    handleClick(record.key);
                },
            };
        },
    };
    const eventsColumns = {
        title: 'Event',
        className: 'no-pointer',
        width: '2.75rem',
        render: (record) => {
            return (
                <CalendarActions>
                    <ModelAvailabilityIcon
                        modelId={record.key}
                        isFetched={availabilityByModel[record.key] && availabilityByModel[record.key].isFetched}
                        isBusy={availabilityByModel[record.key] && availabilityByModel[record.key].isBusy}
                        inEvent={availabilityByModel[record.key] && availabilityByModel[record.key].inEvent}
                    />
                </CalendarActions>
            );
        },
    };
    const nameColumn = {
        title: 'Name',
        key: 'name',
        width: layout.drawer ? '12rem' : '16rem',
        render: (record) => {
            const isFetched = availabilityByModel[record.key] && availabilityByModel[record.key].isFetched;
            const isBusy = availabilityByModel[record.key] && availabilityByModel[record.key].isBusy;
            const isInEvent = availabilityByModel[record.key] && availabilityByModel[record.key].inEvent;
            let className = '';
            if (availabilityInfo.isActive && isFetched) {
                if (isInEvent === 2) {
                    className = 'in-event';
                } else if (isBusy) {
                    className = 'busy';
                } else {
                    className = 'no-busy';
                }
            } else {
                if (isInEvent === 2) {
                    className = 'in-event';
                }
            }
            return (
                <NameCell $mobileView={isMobile} className={className}>
                    {showID && (
                        <div className="id-container" onClick={() => handleClick(record.key)}>
                            <NwTag type="primary">{record.ID}</NwTag>
                        </div>
                    )}
                    <div className="name">
                        <h1 onClick={() => handleClick(record.key)}>{record.name.toLowerCase()}</h1>
                        <div className="name-icons">
                            {record.AL && <ContactAlertPopover contact={record} />}
                            {currentEvent && <PreviousEventsButton modelID={record.ID} eventData={currentEvent} />}
                        </div>
                    </div>
                    <div className="detail" onClick={() => handleClick(record.key)}>
                        <ModelDetailsHeaderAgencies readOnly={multipleSelectionActive} model={record} />
                    </div>
                    {isPackageDrawerCompact && (
                        <div className="compact-summary">
                            {renderModelSummary(record, { smaller: true, showInTown: true })}
                        </div>
                    )}
                </NameCell>
            );
        },
    };
    const nameColumnMobile = {
        title: 'Name',
        key: 'name',
        width: '100%',
        render: (record) => {
            const playlistCount = getPlaylistCount(record);
            return (
                <ModelsTableColumnNameMobile
                    record={record}
                    showID={showID}
                    multipleSelectionActive={multipleSelectionActive}
                    availabilityByModel={availabilityByModel}
                    availabilityInfo={availabilityInfo}
                    playlistCount={playlistCount}
                    onCellClick={handleClick}
                    onOpenDrawer={handleOpenDrawer}
                />
            );
        },
    };
    const emailAddressColumn = {
        title: 'EMailAddress',
        key: 'emailaddress',
        width: '100%',
        render: (record) => {
            return <ModelsTableColumnEmails model={record} />;
        },
    };
    const seasonModelColumn = {
        title: 'Model',
        key: 'season-model',
        width: isMobile ? '100%' : '28rem',
        className: 'season-model-cell',
        render: (_, record) => {
            return <ShowSeasonModelsTableColumnModel record={record} />;
        },
        onCell: (record) => ({
            onClick: () => handleClick(record.key),
        }),
    };
    const seasonAddColumn = {
        title: 'AddToSeason',
        key: 'season-add',
        ...(isMobile ? { width: '100%' } : {}),
        className: 'no-pointer season-add-cell',
        render: (record) => {
            return <ShowSeasonModelsTableColumn model={record} />;
        },
    };
    const featuresColumn = {
        title: 'Features',
        dataIndex: 'features',
        key: 'features',
        //width: "100%",
        width: '100%',
        render: (model) => {
            return renderModelSummary(model, { smaller: layout.drawer, showInTown: scope === 'package' });
        },
        // onCell: (record) => {
        //     return {
        //         onClick: (event) => {
        //             handleClickFeatures(record.key);
        //         },
        //     };
        // },
    };
    const tagsColumn = {
        title: 'Tags',
        dataIndex: 'tags',
        key: 'tags',
        width: '8rem',
        className: 'no-pointer',
        render: (tags) => (
            <TagsContainer>
                {tags.length < 8 ? (
                    tags.map((tag, index) => (
                        <NwTag type="dark" key={index}>
                            {tag}
                        </NwTag>
                    ))
                ) : (
                    <NwTag type="dark" pointer>
                        {tags.length} tags
                    </NwTag>
                )}
            </TagsContainer>
        ),
    };
    const packageColumn = {
        title: 'PackageAction',
        width: isPackageDrawerCompact ? '8rem' : '18rem',
        render: (record) => (
            <ModelsTableColumnPackage
                model={record}
                newlyAddedToPackage={newlyAddedToPackage}
                onAddingBooksToPackage={handleAddBooksToPackage}
                onRemovingBookFromPackage={handleRemoveBookFromPackage}
                onOpenDrawer={handleOpenDrawer}
                modelsAvailability={availabilityByModel}
                isMobile={isMobile}
                isPackageDrawerCompact={isPackageDrawerCompact}
            />
        ),
    };
    // const packageColumnMobile = {
    //     title: 'PackageAction',
    //     width: '10rem',
    //     render: (record) => (
    //         <ModelsTableColumnPackageMobile
    //             record={record}
    //             showID={showID}
    //             multipleSelectionActive={multipleSelectionActive}
    //             modelsAvailability={modelsAvailability}
    //             playlistCount={getPlaylistCount(record)}
    //             onOpenDrawer={handleOpenDrawer}
    //             newlyAddedToPackage={newlyAddedToPackage}
    //             onAddingBooksToPackage={handleAddBooksToPackage}
    //             onRemovingBookFromPackage={handleRemoveBookFromPackage}
    //         />
    //     ),
    // };
    const folderColumn = {
        title: 'FolderAction',
        key: 'folder-add',
        width: isMobile ? '8rem' : '11rem',
        className: 'no-pointer',
        render: (record) => <FolderModelsTableColumn model={record} />,
    };
    const eventColumn = {
        title: 'EventAction',
        width: '100%',
        render: (record) => {
            return (
                <ModelsTableColumnEvent
                    availability={availabilityByModel[record.key]}
                    availabilityInfo={availabilityInfo}
                    model={record}
                    addModelsAs={addModelsAs}
                />
            );
        },
    };
    const nameAndEventColumn = {
        title: 'Name',
        key: 'name',
        width: layout.drawer ? '12rem' : '16rem',
        render: (record) => {
            const isFetched = availabilityByModel[record.key] && availabilityByModel[record.key].isFetched;
            const isBusy = availabilityByModel[record.key] && availabilityByModel[record.key].isBusy;
            const isInEvent = availabilityByModel[record.key] && availabilityByModel[record.key].inEvent;
            let className = '';
            if (availabilityInfo.isActive && isFetched) {
                if (isInEvent === 2) {
                    className = 'in-event';
                } else if (isBusy) {
                    className = 'busy';
                } else {
                    className = 'no-busy';
                }
            } else {
                if (isInEvent === 2) {
                    className = 'in-event';
                }
            }

            return (
                <>
                    <NameCell $mobileView={isMobile} className={className}>
                        {showID && (
                            <div className="id-container">
                                <NwTag type="primary">{record.ID}</NwTag>
                            </div>
                        )}
                        <div className="name">
                            <h1>{record.name.toLowerCase()}</h1>
                            {record.AL && <ContactAlertPopover contact={record} />}
                        </div>
                        <div className="detail">
                            <ModelDetailsHeaderAgencies readOnly={multipleSelectionActive} model={record} />
                        </div>
                        {isMobile && (
                            <>
                                {record.B && <ModelDetailsAge birthday={record.B} nofixedspace small />}
                                <FeaturesContainer $smaller={layout.drawer || isMobile}>
                                    <MainFeatures
                                        contact={record}
                                        showPublicValues
                                        highlightedFeatureIDs={filteredFeatureIDs}
                                    />
                                </FeaturesContainer>
                            </>
                        )}
                    </NameCell>
                    <ModelsTableColumnEvent
                        availability={availabilityByModel[record.key]}
                        availabilityInfo={availabilityInfo}
                        model={record}
                        addModelsAs={addModelsAs}
                    />
                </>
            );
        },
    };
    const nonBookingEventColumn = {
        title: 'EventAction',
        width: '100%',
        render: (record) => {
            return (
                <ModelsTableColumnNonBookingEvent
                    availability={availabilityByModel[record.key]}
                    availabilityInfo={availabilityInfo}
                    model={record}
                />
            );
        },
    };
    const previewColumn = {
        title: 'Preview',
        align: 'center',
        width: '5rem',
        className: 'preview-trigger',
        render: (record) => {
            return <NwIcon icon={byPrefixAndName.fasl['sidebar-flip']} />;
        },
        onCell: (record, rowIndex) => {
            return {
                onClick: (event) => {
                    handleOpenDrawer(record.key);
                },
            };
        },
    };

    const columns = [];

    switch (scope) {
        case 'package':
            if (isMobile) {
                columns.push(thumbnailColumn);
                if (modelAvailability) {
                    columns.push(eventsColumns);
                }
                columns.push(packageColumn);
            } else {
                columns.push(thumbnailColumn);
                if (modelAvailability) {
                    columns.push(eventsColumns);
                }
                if (isPackageDrawerCompact) {
                    // Senza la colonna features serve qualcuno che assorba lo spazio residuo.
                    nameColumn.width = '100%';
                    columns.push(nameColumn);
                } else {
                    columns.push(nameColumn);
                    columns.push(featuresColumn);
                }
                columns.push(packageColumn);
            }
            break;
        case 'event':
            nameColumn.width = '20rem';
            if (!isMobile) {
                columns.push(thumbnailColumn);
            }
            if (modelAvailability) {
                if (isMobile) {
                    eventsColumns.width = '2rem';
                }
                columns.push(eventsColumns);
            }
            if (!isMobile) {
                columns.push(nameColumn);
                columns.push(eventColumn);
            } else {
                nameColumn.width = '100%';
                columns.push(nameAndEventColumn);
            }

            break;
        case 'nonbooking-event':
            nameColumn.width = '20rem';
            columns.push(thumbnailColumn);
            if (modelAvailability) {
                columns.push(eventsColumns);
            }
            if (isMobile) {
                columns.push(nameColumnMobile);
            } else {
                columns.push(nameColumn);
            }
            columns.push(nonBookingEventColumn);
            break;
        case 'folder':
            columns.push(thumbnailColumn);
            if (isMobile) {
                columns.push(nameColumnMobile);
            } else {
                columns.push(nameColumn);
                columns.push(featuresColumn);
            }
            columns.push(folderColumn);
            break;
        case 'mailing-list':
            columns.push(thumbnailColumn);
            if (isMobile) {
                columns.push(nameColumnMobile);
            } else {
                columns.push(nameColumn);
            }
            columns.push(emailAddressColumn);
            columns.push(previewColumn);
            break;
        case 'show-season':
            columns.push(seasonModelColumn);
            columns.push(seasonAddColumn);
            break;
        default:
            if (isMobile) {
                columns.push(thumbnailColumn);
                if (modelAvailability) {
                    columns.push(eventsColumns);
                }
                columns.push(nameColumnMobile);
            } else {
                columns.push(thumbnailColumn);
                if (modelAvailability) {
                    columns.push(eventsColumns);
                }
                columns.push(nameColumn);
                columns.push(featuresColumn);
                columns.push(previewColumn);
            }
            break;
    }

    const rowSelection = {
        selectedRowKeys: selectedItemsIDs,
        onChange: (selectedRowKeys) => {
            cs_dispatch({ type: 'UpdateSelectedItems', newlist: selectedRowKeys });
        },
    };

    return (
        <>
            <StyledModelsTable
                breakpoints={breakpoints}
                rowSelection={multipleSelectionActive && breakpoints > 3 ? rowSelection : null}
                dataSource={dataSource}
                columns={columns}
                size="middle"
                pagination={false}
                showHeader={false}
                tableLayout="fixed"
                className="models-table"
            />
            {isOpenDrawer && (
                <ModelDrawer
                    modelID={drawerId}
                    hideCalendar={areaContext.area === 'scouting'}
                    showRating={areaContext.area === 'scouting'}
                    onClose={handleCloseDrawer}
                    selectedTab={selectedTab}
                />
            )}
            {isOpenFeatureDrawer && (
                <FeaturesDrawerNoData modelID={isOpenFeatureDrawer} onClose={() => setIsOpenFeatureDrawer(false)} />
            )}
            <div ref={loader}>{loadMore && <Loading textBlack small />}</div>
        </>
    );
};

export default ModelsTable;
